import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import HandshakeIcon from '@mui/icons-material/Handshake'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { Outlet, useLoaderData, useNavigate, useParams } from 'react-router-dom'

import BreadCrumbs from '../../root/components/BreadCrumbs'
import IconButtonOutlined from '../../root/components/IconButtonOutlined'
import { HEADER_HEIGHT } from '../../root/components/Header'
import SubHeader from '../../root/components/SubHeader'
import { getOption, useEventsTypeOptions, formatDateTime } from '../../../utils'
import { Card } from '../Dashboard'
import ParticipantStatusTag from '../../events/components/ParticipantStatusTag'
import EventInfoBox from '../../events/components/EventInfoBox'
import { Events, LoaderData } from '../../../types'
import { agendaLoader } from './Agenda.loader'
import SponsorshipStatusTag from '../../abstracts/components/SponsorshipStatusTag'
import { StatusTag } from '../../root/components/StatusTag'

const Background = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.secondaryBackground,
  height: `calc(100% - ${HEADER_HEIGHT} - 1px)`,
  width: '100%',
}))
const HelpBar = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.whiteBackground,
  borderRadius: '10px',
  margin: '0.5rem',
}))

function RegisterInfoCard({
  event,
  eventId,
  url,
}: {
  event: Events
  eventId: string | undefined
  url: string
}) {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const eventsTypeOptions = useEventsTypeOptions(t)

  const type = getOption(eventsTypeOptions, undefined, event.type)

  return (
    <Card
      key={event.id}
      color={type?.color}
      onClick={() => {
        navigate(url)
      }}
      sx={{
        minWidth: { xs: '300px', sm: '350px' },
        minHeight: { xs: 'inherit', md: '240px', lg: '270px' },
        overflowY: { xs: 'scroll', md: 'hidden' },
      }}
      active={event.id === Number(eventId)}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="-webkit-fill-available"
      >
        <h4>{event.name}</h4>
        <Chip
          color="primary"
          sx={{
            backgroundColor: type?.color,
          }}
          label={type?.label}
        />
      </Stack>
      <Stack direction="column" sx={{ display: { xs: 'none', md: 'block' } }}>
        <EventInfoBox
          start={event.start}
          end={event.end}
          location={event.location}
          direction="column"
        />
        <Divider />
      </Stack>
      {!!event.sponsorship_status && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="-webkit-fill-available"
          gap="0.5rem"
        >
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <HandshakeIcon fontSize="small" />
            <Typography variant="h6" sx={{ display: { xs: 'none', md: 'block' } }}>
              {t('État p. en charge')} :
            </Typography>
            <Typography sx={{ textAlign: 'start', display: { xs: 'block', md: 'none' } }}>
              {formatDateTime(event.start)} - {formatDateTime(event.end)} :
            </Typography>
          </Stack>

          <SponsorshipStatusTag status={event.sponsorship_status} />
        </Stack>
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="-webkit-fill-available"
        gap="0.5rem"
      >
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <ConfirmationNumberIcon fontSize="small" />
          <Typography variant="h6" sx={{ display: { xs: 'none', md: 'block' } }}>
            {t('État inscription')} :
          </Typography>
          <Typography sx={{ textAlign: 'start', display: { xs: 'block', md: 'none' } }}>
            {formatDateTime(event.start)} - {formatDateTime(event.end)} :
          </Typography>
        </Stack>
        {event.registration_status ? (
          <ParticipantStatusTag status={event.registration_status} />
        ) : (
          <StatusTag size="medium" absolute={false} position="top" color={'#D9D9D9'}>
            {t('Aucune inscription')}
          </StatusTag>
        )}
      </Stack>
    </Card>
  )
}

export default function Agenda() {
  const { t } = useTranslation('dashboard')
  const { events } = useLoaderData() as LoaderData<ReturnType<typeof agendaLoader>>

  const { eventId } = useParams()

  return (
    <Background>
      <SubHeader>
        <BreadCrumbs
          hasSeparatorEnd
          links={[{ name: t('Mes inscriptions'), pathname: '' }]}
          sx={{ flex: '1 0' }}
        />
        <Typography variant="h3" color="text.primary">
          {t('Gérer mes inscriptions')}
        </Typography>
        <Stack flex="1 0" sx={{ display: { xs: 'none', md: 'flex' } }} />
      </SubHeader>
      <Stack
        flex="1 0"
        direction={{ sm: 'column', md: 'row' }}
        width="100%"
        height="inherit"
        sx={theme => ({
          background: theme.colors.whiteBackground,
        })}
      >
        <Stack
          flexGrow={{ xs: 'auto', md: '0.45 0' }}
          flexBasis="content"
          minWidth={400}
          minHeight={{ xs: 'auto', sm: 'auto', md: 340 }}
          sx={theme => ({
            backgroundColor: theme.colors.greyBackground,
            overflowY: { xs: 'hidden', md: 'auto' },
          })}
          height="100%"
        >
          <HelpBar
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            padding="0.25rem"
            width={{ xs: 'calc(100vw - 1rem)', md: 'calc(100% - 1rem)' }}
          >
            <Stack direction="row" gap="0.5rem" alignItems="center">
              <CalendarMonthIcon fontSize="small" sx={{ margin: '0 0.5rem' }} />
              <IconButtonOutlined size="small">
                <ArrowBackIosIcon fontSize="small" viewBox="0 0 14 24" />
              </IconButtonOutlined>
              <h6>{new Date().getFullYear()}</h6>
              <IconButtonOutlined size="small">
                <ArrowForwardIosIcon fontSize="small" />
              </IconButtonOutlined>
            </Stack>

            <Stack direction="row" alignItems="center" gap="0.2rem" />
          </HelpBar>

          <Stack
            gap="1rem"
            padding="0 1rem 1rem"
            direction={{ xs: 'row', md: 'column' }}
            sx={{ overflowX: { xs: 'auto', md: 'hidden' } }}
          >
            {events?.map(event => (
              <RegisterInfoCard
                key={event.id}
                event={event}
                eventId={eventId}
                url={`/inscriptions/${event.id}`}
              />
            ))}
          </Stack>
        </Stack>

        <Stack flex="1 0" sx={{ overflowY: 'auto' }}>
          <Outlet />
        </Stack>
      </Stack>
    </Background>
  )
}
